import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useLocale } from "@/contexts/LocaleContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const Terms = () => {
  const { locale } = useLocale();
  const isPt = locale.language === "pt";

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="container mx-auto px-4 py-24">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors"
        >
          <ArrowLeft size={18} />
          {isPt ? "Voltar" : "Back"}
        </Link>

        <div className="max-w-3xl mx-auto">
          <h1 className="text-4xl font-bold text-foreground mb-2">
            {isPt ? "Termos de Serviço" : "Terms of Service"}
          </h1>
          <p className="text-muted-foreground mb-8">
            {isPt ? "Última atualização: Janeiro 2025" : "Last updated: January 2025"}
          </p>

          <div className="prose prose-invert max-w-none space-y-8">
            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                {isPt ? "1. Aceitação dos Termos" : "1. Acceptance of Terms"}
              </h2>
              <p className="text-muted-foreground">
                {isPt
                  ? "Ao acessar ou usar o Callyout, você concorda em cumprir estes Termos de Serviço. Se você não concordar com algum destes termos, não deve usar nossos serviços."
                  : "By accessing or using Callyout, you agree to be bound by these Terms of Service. If you do not agree to any of these terms, you should not use our services."}
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                {isPt ? "2. Descrição do Serviço" : "2. Service Description"}
              </h2>
              <p className="text-muted-foreground">
                {isPt
                  ? "O Callyout é uma plataforma de mercado de previsões que utiliza uma moeda virtual chamada CallyCoins. Os CallyCoins não têm valor monetário real e não podem ser trocados por dinheiro real."
                  : "Callyout is a prediction market platform that uses a virtual currency called CallyCoins. CallyCoins have no real monetary value and cannot be exchanged for real money."}
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                {isPt ? "3. Elegibilidade" : "3. Eligibility"}
              </h2>
              <p className="text-muted-foreground">
                {isPt
                  ? "Você deve ter pelo menos 18 anos de idade para usar o Callyout. Ao criar uma conta, você declara e garante que atende a este requisito."
                  : "You must be at least 18 years old to use Callyout. By creating an account, you represent and warrant that you meet this requirement."}
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                {isPt ? "4. Conta do Usuário" : "4. User Account"}
              </h2>
              <p className="text-muted-foreground">
                {isPt
                  ? "Você é responsável por manter a confidencialidade de sua conta e senha. Você concorda em notificar-nos imediatamente sobre qualquer uso não autorizado de sua conta."
                  : "You are responsible for maintaining the confidentiality of your account and password. You agree to notify us immediately of any unauthorized use of your account."}
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                {isPt ? "5. Regras de Conduta" : "5. Rules of Conduct"}
              </h2>
              <p className="text-muted-foreground mb-4">
                {isPt ? "Ao usar o Callyout, você concorda em não:" : "When using Callyout, you agree not to:"}
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2">
                <li>{isPt ? "Criar múltiplas contas" : "Create multiple accounts"}</li>
                <li>{isPt ? "Usar bots ou automação não autorizada" : "Use bots or unauthorized automation"}</li>
                <li>{isPt ? "Manipular ou tentar manipular eventos" : "Manipulate or attempt to manipulate events"}</li>
                <li>{isPt ? "Assediar ou ameaçar outros usuários" : "Harass or threaten other users"}</li>
                <li>{isPt ? "Violar leis ou regulamentos aplicáveis" : "Violate applicable laws or regulations"}</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                {isPt ? "6. CallyCoins e Previsões" : "6. CallyCoins and Predictions"}
              </h2>
              <p className="text-muted-foreground">
                {isPt
                  ? "CallyCoins podem ser adquiridos através da nossa loja e são para fins de entretenimento. CallyCoins não têm valor monetário real e não podem ser trocados por dinheiro real. As previsões são finais e não podem ser canceladas após a confirmação. Os resultados dos eventos são determinados pela equipe do Callyout e são finais."
                  : "CallyCoins can be purchased through our store and are for entertainment purposes only. CallyCoins have no real monetary value and cannot be exchanged for real money. Predictions are final and cannot be cancelled after confirmation. Event outcomes are determined by the Callyout team and are final."}
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                {isPt ? "7. Política de Reembolso" : "7. Refund Policy"}
              </h2>
              <p className="text-muted-foreground mb-4">
                {isPt
                  ? "Todas as compras de CallyCoins são finais e não reembolsáveis. Ao fazer uma compra, você reconhece e concorda que:"
                  : "All CallyCoin purchases are final and non-refundable. By making a purchase, you acknowledge and agree that:"}
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2">
                <li>{isPt ? "CallyCoins são bens digitais virtuais sem valor monetário real" : "CallyCoins are virtual digital goods with no real monetary value"}</li>
                <li>{isPt ? "Nenhum reembolso será fornecido por qualquer motivo" : "No refunds will be provided for any reason"}</li>
                <li>{isPt ? "CallyCoins não utilizados não podem ser trocados por dinheiro" : "Unused CallyCoins cannot be exchanged for money"}</li>
                <li>{isPt ? "Em caso de encerramento da conta, os CallyCoins restantes serão perdidos" : "In case of account termination, remaining CallyCoins will be forfeited"}</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                {isPt ? "8. Suspensão e Encerramento" : "8. Suspension and Termination"}
              </h2>
              <p className="text-muted-foreground">
                {isPt
                  ? "Reservamo-nos o direito de suspender ou encerrar sua conta a qualquer momento, por qualquer motivo, incluindo violação destes termos."
                  : "We reserve the right to suspend or terminate your account at any time, for any reason, including violation of these terms."}
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                {isPt ? "9. Isenção de Responsabilidade" : "9. Disclaimer"}
              </h2>
              <p className="text-muted-foreground">
                {isPt
                  ? 'O Callyout é fornecido "como está" sem garantias de qualquer tipo. Não nos responsabilizamos por perdas de CallyCoins ou interrupções no serviço.'
                  : 'Callyout is provided "as is" without warranties of any kind. We are not responsible for losses of CallyCoins or service interruptions.'}
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                {isPt ? "10. Alterações nos Termos" : "10. Changes to Terms"}
              </h2>
              <p className="text-muted-foreground">
                {isPt
                  ? "Podemos modificar estes termos a qualquer momento. Continuando a usar o serviço após as alterações, você aceita os termos modificados."
                  : "We may modify these terms at any time. By continuing to use the service after changes, you accept the modified terms."}
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-foreground mb-4">
                {isPt ? "11. Contato" : "11. Contact"}
              </h2>
              <p className="text-muted-foreground">
                {isPt
                  ? "Se você tiver dúvidas sobre estes termos, entre em contato conosco pelo e-mail:"
                  : "If you have questions about these terms, please contact us at:"}
              </p>
              <a 
                href="mailto:support@duvdex.com" 
                className="text-primary hover:underline mt-2 inline-block"
              >
                support@duvdex.com
              </a>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Terms;